import Col from 'react-bootstrap/Col';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';

import ButtonDisclosures from '../../../atoms/button-disclosures';
import CardModule from '../../shared/card-module';
import TableBasic from '../../../atoms/table-basic/index';
import format from '../../../../utils/format';
import styles from './fees-and-expenses.module.scss';
import { FUND_TYPES } from '../../../../stores/profile/get-fund-type';
import { augment } from '../../../../utils/GlobalCssProxy';
import { className } from '../../../../utils/GlobalCssProxy';

const s = augment(styles);

const formatExpenseRatio = (expenses, isCIT, isCITFAFund) => {
  //TIAA-12416 CIT Fixed Annuities will not have gross/net data, so we display N/A instead of --
  let formattedExpenses;
  if (isCITFAFund) return 'N/A';
  if (isCIT) {
    formattedExpenses = `${format.number(expenses, { formatString: '0,0.00[0]' })}%`;
  } else {
    formattedExpenses = format.percent(expenses);
  }

  return formattedExpenses;
};

const FeesAndExpenses = ({ expenses, heading, fundType, text, isHideForPrint, isCIT, isCITFAFund }) => {
  const isAfterTaxAnnuity = fundType === FUND_TYPES.afterTaxAnnuity;
  return (
    <CardModule
      data-analytics-componenttype="third-width-component"
      data-analytics-id="TW_FeesAndExpenses"
      data-analytics-section="component"
      className={s('print-fees-expenses')}
      {...className(isHideForPrint ? 'd-print-none' : '')}
    >
      <CardModule.Header>{heading}</CardModule.Header>
      <CardModule.Body>
        {(fundType === FUND_TYPES.retirePlus || fundType === FUND_TYPES.retirePlusPro) ?
          <ExpenseSummary feesExpensesSummary={expenses} summaryText={text} />
          : <Row as="section">
            {
              !isAfterTaxAnnuity &&
              <Col as="dl" sm={6} md={12} lg={6}>
                <dt
                  className={s(
                    'font-weight-normal',
                    'd-flex',
                    'justify-content-sm-center'
                  )}
                >
                  <span className={s('sub-info')}>
                    Gross Expense Ratio
                  </span>
                </dt>
                <dd
                  className={s(
                    'font-family-agenda',
                    'text-nowrap',
                    'percentage',
                    'mb-0',
                    'text-secondary',
                    'd-flex',
                    'justify-content-sm-center'
                  )}
                >
                  {formatExpenseRatio(expenses.grossExpenseRatio, isCIT, isCITFAFund)}
                </dd>
              </Col>
            }
            <Col as="dl" sm={6} md={12} lg={6}>
              <dt
                className={s(
                  'font-weight-normal',
                  'd-flex',
                  'justify-content-sm-center'
                )}
              >
                <span className={s('sub-info')}>
                  Net Expense Ratio
                </span>
              </dt>
              <dd
                className={s(
                  'font-family-agenda',
                  'text-nowrap',
                  'percentage',
                  'mb-0',
                  'text-secondary',
                  'd-flex',
                  'justify-content-sm-center'
                )}
              >
                {formatExpenseRatio(expenses.netExpenseRatio, isCIT, isCITFAFund)}
              </dd>
            </Col>
            {
              isAfterTaxAnnuity &&
              <Col as="dl" sm={6} md={12} lg={6}>
                <dt
                  className={s(
                    'font-weight-normal',
                    'd-flex',
                    'justify-content-sm-center'
                  )}
                >
                  Total Expense Ratio
                </dt>
                <dd
                  className={s(
                    'font-family-agenda',
                    'text-nowrap',
                    'percentage',
                    'mb-0',
                    'text-secondary',
                    'd-flex',
                    'justify-content-sm-center'
                  )}
                >
                  {format.percent(expenses.totalExpenseRatio)}
                </dd>
              </Col>
            }
          </Row>}
        {(fundType === FUND_TYPES.retirePlus || fundType === FUND_TYPES.retirePlusPro)
          && (<Row>
            <ExpenseBreakdown feesExpensesBreakdown={expenses} />
          </Row>)}
      </CardModule.Body>
      <CardModule.Footer className={s('text-right')}>
        {/*Fees and expenses and performance returns use the same disclosure terms*/}
        <ButtonDisclosures module={'performance-returns'} />
      </CardModule.Footer>
    </CardModule>
  );
};

FeesAndExpenses.defaultProps = {
  expenses: {},
  heading: 'Fees & Expenses',
  isCIT: false,
  isCITFAFund: false
};

FeesAndExpenses.propTypes = {
  expenses: PropTypes.shape({
    netExpenseRatio: PropTypes.number,
    grossExpenseRatio: PropTypes.number,
    estAnnualExpenses: PropTypes.number,
    totalExpenseRatio: PropTypes.number
  }),
  heading: PropTypes.string,
  fundType: PropTypes.string,
  text: PropTypes.string,
  isHideForPrint: PropTypes.bool,
  isCIT: PropTypes.bool,
  isCITFAFund: PropTypes.bool
};

const ExpenseBreakdown = ({ feesExpensesBreakdown }) => {
  return (
    <TableBasic flush className={s('border-top-dashed')}>
      <caption className={s('text-left', 'sr-only')}>
        Fees and Expense for retire plus
      </caption>
      <thead>
        <tr>
          <th scope="row" className={s('text-left')} colSpan="2">
            Fees Related to the Model Service
          </th>
        </tr>
      </thead>
      <tbody>
        <tr className={s('print-font-size')}>
          <th scope="row" className={s('text-left')}>Recordkeeping Fee</th>
          <td className={s('text-right')}>
            {format.number(feesExpensesBreakdown.recordKeepingFees, { formatString: '0,0.00[000]' })}
          </td>
        </tr>
        <tr className={s('print-font-size')}>
          <th scope="row" className={s('text-left')}>Program Sponsor Fee</th>
          <td className={s('text-right')}>
            {format.number(feesExpensesBreakdown.programSponsorFees, { formatString: '0,0.00[000]' })}
          </td>
        </tr>
        <tr className={s('print-font-size')}>
          <th scope="row" className={s('text-left')}>Plan Advisor Fee</th>
          <td className={s('text-right')}>
            {format.number(feesExpensesBreakdown.planAdvisorFees, { formatString: '0,0.00[000]' })}
          </td>
        </tr>
        <tr className={s('print-font-size')}>
          <th scope="row" className={s('text-left')}>Other Expenses</th>
          <td className={s('text-right')}>
            {format.number(feesExpensesBreakdown.otherExpenses, { formatString: '0,0.00[000]' })}
          </td>
        </tr>
      </tbody>
    </TableBasic>
  );
};

ExpenseBreakdown.defaultProps = {
  feesExpensesBreakdown: {}
};

ExpenseBreakdown.propTypes = {
  feesExpensesBreakdown: PropTypes.shape({
    recordKeepingFees: PropTypes.number,
    programSponsorFees: PropTypes.number,
    planAdvisorFees: PropTypes.number,
    otherExpenses: PropTypes.number
  }
  )
};

const ExpenseSummary = ({ feesExpensesSummary, summaryText }) => {
  return (
    <Row as="section">
      <Row>
        <div className={s(
          'd-flex',
          'felx-row',
          'align-items-center',
          'expense-summary'
        )}>
          <Row
            className={s(
              'font-weight-normal',
              'align-self-end',
              'mb-4',
              'order-2',
              'percentage-label',
              'ml-2',
              'print-m-none'
            )}
          >
            Total Operating Expense Ratio including Fees
          </Row>
          <Row
            className={s(
              'font-family-agenda',
              'text-nowrap',
              'percentage',
              'mb-0',
              'text-secondary',
              'd-block',
              'justify-content-sm-center',
              'order-1',
              'mr-2',
              'ml-4'
            )}
          >
            {format.number(feesExpensesSummary.grossNetExpenses, { formatString: '0,0.00[000]' }) + '%'}
          </Row>
        </div>
      </Row>
      <Row as="section">
        <p className={s(
          'mt-4',
          'mb-6',
          'p-3',
          'fe-paragraph'
        )}>
          {summaryText}
        </p>
      </Row>
    </Row>
  );
};

ExpenseSummary.propTypes = {
  feesExpensesSummary: PropTypes.shape({
    grossNetExpenses: PropTypes.number
  }
  ),
  summaryText: PropTypes.string
};

export default FeesAndExpenses;
